const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

export function project(lon: number, lat: number, W = 960, H = 480): [number, number] {
  const x = (lon + 180) * (W / 360);
  const y = (90 - lat) * (H / 180);
  return [x, y];
}

function slerpArc(
  x1: number, y1: number, z1: number,
  x2: number, y2: number, z2: number,
  omega: number,
  steps: number
): [number, number][] {
  const dot = x1 * x2 + y1 * y2 + z1 * z2;
  let px2 = x2 - dot * x1;
  let py2 = y2 - dot * y1;
  let pz2 = z2 - dot * z1;
  const plen = Math.sqrt(px2 * px2 + py2 * py2 + pz2 * pz2);
  if (plen > 1e-10) { px2 /= plen; py2 /= plen; pz2 /= plen; }

  const pts: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const theta = (i / steps) * omega;
    const px = Math.cos(theta) * x1 + Math.sin(theta) * px2;
    const py = Math.cos(theta) * y1 + Math.sin(theta) * py2;
    const pz = Math.cos(theta) * z1 + Math.sin(theta) * pz2;
    const lat = Math.atan2(pz, Math.sqrt(px * px + py * py)) * RAD;
    const lon = Math.atan2(py, px) * RAD;
    pts.push([lon, lat]);
  }
  return pts;
}

function crossesAntimeridian(pts: [number, number][]): boolean {
  for (let i = 1; i < pts.length; i++) {
    if (Math.abs(pts[i][0] - pts[i - 1][0]) > 180) return true;
  }
  return false;
}

export function greatCirclePoints(
  lon1: number, lat1: number,
  lon2: number, lat2: number,
  steps = 80
): [number, number][] {
  const p1 = lat1 * DEG, l1 = lon1 * DEG;
  const p2 = lat2 * DEG, l2 = lon2 * DEG;

  const x1 = Math.cos(p1) * Math.cos(l1);
  const y1 = Math.cos(p1) * Math.sin(l1);
  const z1 = Math.sin(p1);
  const x2 = Math.cos(p2) * Math.cos(l2);
  const y2 = Math.cos(p2) * Math.sin(l2);
  const z2 = Math.sin(p2);

  const dot = Math.max(-1, Math.min(1, x1 * x2 + y1 * y2 + z1 * z2));
  const shortOmega = Math.acos(dot);
  const longOmega = 2 * Math.PI - shortOmega;

  const shortPts = slerpArc(x1, y1, z1, x2, y2, z2, shortOmega, steps);
  if (!crossesAntimeridian(shortPts)) return shortPts;

  return slerpArc(x1, y1, z1, -x2, -y2, -z2, longOmega, steps);
}

export function arcToPolylines(pts: [number, number][], W = 960): string[] {
  const segments: string[][] = [];
  let current: string[] = [];

  for (let i = 0; i < pts.length; i++) {
    const [lon, lat] = pts[i];
    const [x, y] = project(lon, lat, W);
    if (i > 0) {
      const [prevLon] = pts[i - 1];
      const [prevX] = project(prevLon, pts[i - 1][1], W);
      if (Math.abs(x - prevX) > W / 2) {
        if (current.length > 0) segments.push(current);
        current = [];
      }
    }
    current.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  if (current.length > 0) segments.push(current);
  return segments.map(s => s.join(' '));
}
